import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined'
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, ListSubheader, MenuItem, TextField, Tooltip } from '@mui/material'
import React from 'react'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import type { ExcelImportFieldStatus } from '../../../model/kartera-api-model'
import { formatCount, STATUS_TONES } from '../common/policy-import-common'
import {
    COLUMNS,
    DATE_FORMATS,
    EXCEL_ACCEPT,
    INSTRUCTIONS,
    TEMPLATE_DOWNLOAD_NAME,
    TEMPLATE_URL,
    usePolicyImportExcelModel,
} from './policy-import-excel-model'
import type { ExcelCell } from './policy-import-excel-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }
const primaryBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border-0 bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40'
const outlineBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover'

const RowStatusIcon = ({ status }: { status: ExcelImportFieldStatus }) => {
    if (status === 'error') return <HighlightOffIcon className="text-k-danger" style={{ fontSize: 18 }} />
    if (status === 'warning') return <ReportProblemOutlinedIcon className="text-tone-orange-ink" style={{ fontSize: 18 }} />
    return <CheckCircleOutlineIcon className="text-k-ok" style={{ fontSize: 18 }} />
}

const Cell = ({ cell }: { cell: ExcelCell }) => {
    const content = (
        <span className={`inline-flex max-w-full items-center gap-1 truncate ${STATUS_TONES[cell.status]} ${cell.status !== 'ok' ? 'font-semibold' : ''}`}>
            {cell.status === 'error' && <ErrorOutlineIcon style={{ fontSize: 14 }} />}
            {cell.status === 'warning' && <ReportProblemOutlinedIcon style={{ fontSize: 14 }} />}
            <span className="truncate">{cell.value || (cell.status === 'ok' ? '—' : 'Vacío')}</span>
        </span>
    )
    if (cell.status === 'ok' || !cell.message) return content
    return <Tooltip title={cell.message}>{content}</Tooltip>
}

const PolicyImportExcel = () => {
    const model = usePolicyImportExcelModel()

    return (
        <KarteraAdminTemplate loading={false} title="Importar desde Excel">
            <Dialog open={model.showIssues} onClose={model.closeIssues} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Observaciones del Excel</DialogTitle>
                <DialogContent>
                    <div className="flex flex-col gap-3 text-sm text-k-ink-2">
                        <p className="m-0">Tras analizar el Excel detectamos lo siguiente:</p>
                        {model.summary.error > 0 && (
                            <p className="m-0 flex gap-2 rounded-xl bg-k-danger-soft px-3 py-2.5 text-k-danger-ink">
                                <HighlightOffIcon style={{ fontSize: 18 }} />
                                <span><strong>{formatCount(model.summary.error)} {model.summary.error === 1 ? 'fila con errores' : 'filas con errores'}</strong>, que tendrás que corregir antes de poder importar.</span>
                            </p>
                        )}
                        {model.summary.warning > 0 && (
                            <p className="m-0 flex gap-2 rounded-xl bg-tone-orange px-3 py-2.5 text-tone-orange-ink">
                                <ReportProblemOutlinedIcon style={{ fontSize: 18 }} />
                                <span><strong>{formatCount(model.summary.warning)} {model.summary.warning === 1 ? 'fila con advertencias' : 'filas con advertencias'}</strong>. No impiden importar, pero te aconsejamos atenderlas antes de continuar.</span>
                            </p>
                        )}
                    </div>
                </DialogContent>
                <DialogActions className="px-6 pb-5">
                    <button type="button" className={primaryBtnClass} onClick={model.closeIssues}>Entendido</button>
                </DialogActions>
            </Dialog>

            <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Importar desde Excel"
                    description="Carga el formato de importación, revisa la validación y luego importa tus pólizas."
                    backTo={model.backTo}
                    backLabel="Importar pólizas"
                    actions={(
                        <a href={TEMPLATE_URL} download={TEMPLATE_DOWNLOAD_NAME} className={outlineBtnClass}>
                            <DownloadOutlinedIcon style={{ fontSize: 18 }} />
                            Descargar formato de Excel
                        </a>
                    )}
                />

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Archivo</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Formato de la fecha"
                            helperText="El formato de fecha que usaste en el Excel (dd = día, MM = mes, yyyy = año, hh = hora, mm = minuto, ss = segundo)."
                            value={model.dateFormat}
                            disabled={model.busy}
                            onChange={(event) => model.changeDateFormat(event.target.value)}
                            slotProps={{ select: selectMenu }}
                        >
                            {DATE_FORMATS.flatMap((group) => [
                                <ListSubheader key={group.group}>{group.group}</ListSubheader>,
                                ...group.options.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>),
                            ])}
                        </TextField>

                        <label className={`flex items-center gap-3.5 rounded-2xl border-[1.5px] border-dashed border-k-line-strong bg-k-surface p-4 ${model.busy ? 'pointer-events-none opacity-60' : 'cursor-pointer hover:border-k-primary hover:bg-k-hover'}`}>
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-tone-teal text-tone-teal-ink">
                                {model.processing ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <TableViewOutlinedIcon />}
                            </span>
                            <span className="flex min-w-0 flex-col gap-0.5">
                                <span className="text-sm font-extrabold text-k-ink">
                                    {model.processing ? 'Analizando el Excel…' : model.originalName ? 'Cargar otro Excel' : 'Carga el Excel con las pólizas'}
                                </span>
                                <span className="truncate text-xs text-k-muted">{model.originalName || 'Archivo .xlsx con el formato de importación'}</span>
                            </span>
                            <input
                                type="file"
                                accept={EXCEL_ACCEPT}
                                className="sr-only"
                                disabled={model.busy}
                                onChange={(event) => { model.uploadExcel(event.target.files?.[0]); event.target.value = '' }}
                            />
                        </label>
                    </div>
                </section>

                {model.hasData && (
                    <section className="flex flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                        <div className="flex flex-wrap items-center gap-2 border-b border-k-line px-4 py-3.5">
                            <h2 className="k-card-title mr-auto">Validación</h2>
                            <span className="inline-flex items-center gap-1 rounded-full bg-k-ok-soft px-2.5 py-1 text-xs font-bold text-k-ok"><CheckCircleOutlineIcon style={{ fontSize: 14 }} />{formatCount(model.summary.ok)} correctas</span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-tone-orange px-2.5 py-1 text-xs font-bold text-tone-orange-ink"><ReportProblemOutlinedIcon style={{ fontSize: 14 }} />{formatCount(model.summary.warning)} advertencias</span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-k-danger-soft px-2.5 py-1 text-xs font-bold text-k-danger-ink"><HighlightOffIcon style={{ fontSize: 14 }} />{formatCount(model.summary.error)} errores</span>
                        </div>

                        <div className="max-h-[560px] overflow-auto">
                            <table className="w-max min-w-full border-collapse text-[13px]">
                                <thead>
                                    <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                                        <th className="sticky left-0 top-0 z-20 w-12 bg-k-surface px-3 py-3"><span className="sr-only">Estado</span></th>
                                        {COLUMNS.map((column) => (
                                            <th key={column.key} className={`sticky top-0 z-10 whitespace-nowrap bg-k-surface px-2.5 py-3 ${column.align === 'right' ? 'text-right' : ''}`} style={{ minWidth: column.width }}>
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {model.rows.length === 0 && (
                                        <tr><td colSpan={COLUMNS.length + 1} className="px-4 py-12 text-center text-sm text-k-muted">El Excel no contiene pólizas.</td></tr>
                                    )}
                                    {model.rows.map((row) => (
                                        <tr key={row.id} className={`border-t border-k-line ${row.status === 'error' ? 'bg-k-danger-soft/40' : row.status === 'warning' ? 'bg-tone-orange' : ''}`}>
                                            <td className="sticky left-0 z-[5] bg-k-surface px-3 py-2"><RowStatusIcon status={row.status} /></td>
                                            {COLUMNS.map((column) => (
                                                <td key={column.key} className={`max-w-[280px] px-2.5 py-2 ${column.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                                                    <Cell cell={row.cells[column.key]} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-k-line px-4 py-3 text-sm text-k-muted">
                            <span className="hidden md:inline">Mostrando {formatCount(model.rangeFrom)} a {formatCount(model.rangeTo)} de {formatCount(model.totalRows)} filas</span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    aria-label="Página anterior"
                                    disabled={model.page <= 1}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                    onClick={() => model.setPage(model.page - 1)}
                                >
                                    <ChevronLeftIcon fontSize="small" />
                                </button>
                                {model.pages.map((number) => (
                                    <button
                                        key={number}
                                        type="button"
                                        aria-current={number === model.page ? 'page' : undefined}
                                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium ${number === model.page
                                            ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                            : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                        onClick={() => model.setPage(number)}
                                    >
                                        {number}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    aria-label="Página siguiente"
                                    disabled={model.page >= model.totalPages}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                    onClick={() => model.setPage(model.page + 1)}
                                >
                                    <ChevronRightIcon fontSize="small" />
                                </button>
                            </div>
                        </div>
                    </section>
                )}

                {model.hasData && (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-col gap-1 text-sm">
                            {model.summary.error > 0 && <span className="text-k-danger-ink">Corrige todos los errores en el Excel y vuelve a cargarlo para poder importar.</span>}
                            {model.summary.error === 0 && model.summary.warning > 0 && <span className="text-tone-orange-ink">Las advertencias permiten importar, pero te aconsejamos atenderlas.</span>}
                            <span className="text-xs text-k-muted">Pasa el cursor sobre una celda marcada para ver el detalle.</span>
                        </div>
                        <button type="button" className={primaryBtnClass} onClick={model.importPolicies} disabled={!model.canImport}>
                            {model.importing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckIcon />}
                            {model.importing ? 'Importando…' : 'Importar pólizas'}
                        </button>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Instrucciones</h2>
                    <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
                        {INSTRUCTIONS.map((text, index) => (
                            <li key={text} className="flex items-start gap-3 text-sm text-k-ink-2">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-k-accent-soft text-xs font-extrabold text-k-accent-ink">{index + 1}</span>
                                <span className="pt-0.5">{text}</span>
                            </li>
                        ))}
                    </ol>
                </section>
            </div>
        </KarteraAdminTemplate>
    )
}

export default PolicyImportExcel
